/*--------------------------------------------------------------
# Video Playlist Feed
# - selecting a list item swaps the active player panel
# - up/down arrows scroll the list
# - the blue track indicator reflects the scroll position
# - below lg, selecting a list item expands its panel inline as an
#   accordion (plus becomes minus) instead of jumping to the player
--------------------------------------------------------------*/

(function () {
	function initPlaylist(root) {
		var thumbs = Array.prototype.slice.call(root.querySelectorAll('.video-playlist__thumb'));
		var panels = Array.prototype.slice.call(root.querySelectorAll('.video-playlist__panel'));
		var list = root.querySelector('.video-playlist__list');
		var bar = root.querySelector('.video-playlist__progress-bar');
		var arrowUp = root.querySelector('.video-playlist__arrow--up');
		var arrowDown = root.querySelector('.video-playlist__arrow--down');

		if (!thumbs.length || !panels.length) {
			return;
		}

		function setActive(index) {
			thumbs.forEach(function (thumb) {
				var active = thumb.getAttribute('data-index') === String(index);
				thumb.classList.toggle('is-active', active);
				thumb.setAttribute('aria-selected', active ? 'true' : 'false');
			});
			panels.forEach(function (panel) {
				var active = panel.getAttribute('data-index') === String(index);
				panel.classList.toggle('is-active', active);
				if (active) {
					panel.removeAttribute('hidden');
				} else {
					panel.setAttribute('hidden', '');
				}
			});
		}

		var stage = root.querySelector('.video-playlist__stage');
		var mobile = window.matchMedia('(max-width: 991.98px)');
		var desktop = window.matchMedia('(min-width: 992px)');

		function getPanel(index) {
			return panels.filter(function (panel) {
				return panel.getAttribute('data-index') === String(index);
			})[0];
		}

		function getThumb(index) {
			return thumbs.filter(function (thumb) {
				return thumb.getAttribute('data-index') === String(index);
			})[0];
		}

		// The duration badge reads as the single-letter "10 m" everywhere,
		// except while its item is the open mobile accordion panel, where it
		// spells out "10 minutes watch".
		function syncThumbBadge(thumb) {
			var badge = thumb.querySelector('.video-playlist__badge');
			if (!badge) {
				return;
			}
			var isOpen = mobile.matches && thumb.classList.contains('is-active');
			var label = badge.getAttribute(isOpen ? 'data-label-open' : 'data-label-short');
			if (label) {
				badge.textContent = label;
			}
		}

		function syncAllBadges() {
			thumbs.forEach(syncThumbBadge);
		}

		// Below lg the panel expands inline under its thumb rather than living in
		// the stage, so it needs an open/close animation and DOM move of its own.
		function openPanelMobile(panel) {
			panel.removeAttribute('hidden');
			panel.classList.add('is-active');
			panel.style.maxHeight = '0px';
			window.requestAnimationFrame(function () {
				window.requestAnimationFrame(function () {
					panel.style.maxHeight = panel.scrollHeight + 'px';
				});
			});
		}

		function closePanelMobile(panel) {
			if (panel.hasAttribute('hidden')) {
				return;
			}
			panel.style.maxHeight = panel.scrollHeight + 'px';
			window.requestAnimationFrame(function () {
				panel.style.maxHeight = '0px';
			});
			panel.addEventListener('transitionend', function onEnd(event) {
				if (event.propertyName !== 'max-height') {
					return;
				}
				panel.removeEventListener('transitionend', onEnd);
				if (panel.style.maxHeight === '0px') {
					panel.setAttribute('hidden', '');
					panel.classList.remove('is-active');
					panel.style.maxHeight = '';
				}
			});
		}

		// Puts every panel back in the stage, in its original order, ready for the
		// side-by-side desktop layout.
		function restorePanelsToStage() {
			if (!stage) {
				return;
			}
			panels.forEach(function (panel) {
				panel.style.maxHeight = '';
				stage.appendChild(panel);
			});
		}

		function toggleAccordion(index) {
			var thumb = getThumb(index);
			var panel = getPanel(index);
			if (!thumb || !panel) {
				return;
			}
			var wasOpen = thumb.classList.contains('is-active') && !panel.hasAttribute('hidden');

			panels.forEach(function (otherPanel) {
				if (otherPanel !== panel) {
					closePanelMobile(otherPanel);
				}
			});
			thumbs.forEach(function (otherThumb) {
				var active = otherThumb === thumb && !wasOpen;
				otherThumb.classList.toggle('is-active', active);
				otherThumb.setAttribute('aria-selected', active ? 'true' : 'false');
			});
			syncAllBadges();

			if (wasOpen) {
				closePanelMobile(panel);
			} else {
				thumb.insertAdjacentElement('afterend', panel);
				openPanelMobile(panel);
			}
		}

		// Below lg the list is a normal stacked block (no height cap needed); at lg+
		// it sits beside the player and should match its height, scrolling internally
		// rather than stretching __body to fit every item.
		function syncListHeight() {
			if (!stage || !list) {
				return;
			}
			if (desktop.matches) {
				root.style.setProperty('--vp-list-height', stage.offsetHeight + 'px');
			} else {
				root.style.removeProperty('--vp-list-height');
			}
		}

		if (stage && window.ResizeObserver) {
			new ResizeObserver(syncListHeight).observe(stage);
		} else {
			window.addEventListener('resize', syncListHeight);
		}
		syncListHeight();

		thumbs.forEach(function (thumb) {
			thumb.addEventListener('click', function () {
				var index = thumb.getAttribute('data-index');
				// On tablet and below, expand the panel inline as an accordion
				// instead of swapping and jumping to a separate player.
				if (mobile.matches) {
					toggleAccordion(index);
				} else {
					setActive(index);
				}
			});
		});

		// Keep the accordion/stage arrangement in sync when crossing the lg breakpoint.
		function handleBreakpointChange(event) {
			if (event.matches) {
				var activeThumb = thumbs.filter(function (thumb) {
					return thumb.classList.contains('is-active');
				})[0];
				var activePanel = panels.filter(function (panel) {
					return panel.classList.contains('is-active');
				})[0];
				if (activeThumb && activePanel) {
					activeThumb.insertAdjacentElement('afterend', activePanel);
					activePanel.removeAttribute('hidden');
					activePanel.style.maxHeight = '';
				}
			} else {
				restorePanelsToStage();
			}
			syncAllBadges();
		}
		if (mobile.addEventListener) {
			mobile.addEventListener('change', handleBreakpointChange);
		} else if (mobile.addListener) {
			mobile.addListener(handleBreakpointChange);
		}

		// Place the initially active panel inline under its thumb if we're
		// already below lg on load.
		if (mobile.matches) {
			var initialThumb = thumbs.filter(function (thumb) {
				return thumb.classList.contains('is-active');
			})[0];
			var initialPanel = panels.filter(function (panel) {
				return panel.classList.contains('is-active');
			})[0];
			if (initialThumb && initialPanel) {
				initialThumb.insertAdjacentElement('afterend', initialPanel);
			}
		}
		syncAllBadges();

		if (!list) {
			return;
		}

		function isVertical() {
			return list.scrollHeight - list.clientHeight > 1;
		}

		function updateProgress() {
			if (!bar) {
				return;
			}
			var scrollable, size, offset;
			if (isVertical()) {
				scrollable = list.scrollHeight;
				size = list.clientHeight;
				offset = list.scrollTop;
			} else {
				scrollable = list.scrollWidth;
				size = list.clientWidth;
				offset = list.scrollLeft;
			}
			if (scrollable <= size) {
				bar.style.height = '100%';
				bar.style.top = '0';
			} else {
				bar.style.height = (size / scrollable) * 100 + '%';
				bar.style.top = (offset / scrollable) * 100 + '%';
			}

			if (arrowUp) {
				arrowUp.disabled = offset <= 0;
			}
			if (arrowDown) {
				arrowDown.disabled = offset + size >= scrollable - 1;
			}
		}

		function scrollByStep(direction) {
			var step = thumbs[0] ? thumbs[0].offsetHeight + 24 : 120;
			if (isVertical()) {
				list.scrollBy({ top: step * direction, behavior: 'smooth' });
			} else {
				var stepX = thumbs[0] ? thumbs[0].offsetWidth + 24 : 320;
				list.scrollBy({ left: stepX * direction, behavior: 'smooth' });
			}
		}

		if (arrowUp) {
			arrowUp.addEventListener('click', function () {
				scrollByStep(-1);
			});
		}
		if (arrowDown) {
			arrowDown.addEventListener('click', function () {
				scrollByStep(1);
			});
		}

		list.addEventListener('scroll', function () {
			window.requestAnimationFrame(updateProgress);
		});
		window.addEventListener('resize', updateProgress);
		updateProgress();
	}

	function init() {
		Array.prototype.slice
			.call(document.querySelectorAll('.video-playlist'))
			.forEach(initPlaylist);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
